/**
 * The pieces an invoice and a payment receipt both draw.
 *
 * Both detail views show the same document shape — a masthead, who is billing
 * whom, the facts, the lines, the total — so the shape lives here once. It
 * also means the PRINT rules only have to be right in one place: the `doc-*`
 * classes below are what `@media print` in `index.css` keys off, and a second
 * copy of this markup would print subtly differently from the first.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import type { DocumentBrand, DocumentEvent, DocumentLine } from '@simpleacs/shared';
import { Button, Pill, TBody, THead, TRow, Table } from '../../components/ui';
import { formatMoney, formatTimestamp } from '../../lib/format';

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="print:hidden mb-4 inline-flex items-center gap-2 text-sm text-muted hover:text-ink"
    >
      <ArrowLeft className="size-4" />
      {children}
    </Link>
  );
}

/**
 * Print and Download, side by side and both hidden on paper.
 *
 * Two ways out on purpose. Print is the browser's own, which is what somebody
 * with the page open and a printer in the corridor reaches for. The PDF is
 * generated by the API from the same rows — for attaching to an email, where
 * "print to PDF" would give you whatever that browser's margins happened to
 * be. NEITHER is stored: the operator's rule is that a document is rendered on
 * request, so a corrected note cannot leave a stale copy behind.
 */
export function DocumentActions({ pdfHref }: { pdfHref: string }) {
  return (
    <div className="print:hidden flex items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        icon={<Printer className="size-3.5" />}
        onClick={() => window.print()}
      >
        Print
      </Button>
      {/*
        A plain link rather than a fetch-and-blob: the session is a same-origin
        cookie, so the browser's own download does the whole job — including
        honouring the filename the API puts in Content-Disposition.
      */}
      <a
        href={pdfHref}
        className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-line bg-surface-2 px-3 text-xs font-medium text-ink transition-colors hover:bg-surface-3"
      >
        <Download className="size-3.5" />
        Download PDF
      </a>
    </div>
  );
}

/** The masthead: the operator's logo or name, and what this document is. */
export function BrandHeader({
  brand,
  title,
  reference,
}: {
  brand: DocumentBrand;
  title: string;
  reference: string;
}) {
  return (
    <header
      className="doc-band flex flex-wrap items-start justify-between gap-4 border-b-2 px-5 py-4"
      style={{ borderColor: brand.accentColor ?? 'var(--primary)' }}
    >
      <div className="flex items-center gap-3">
        {brand.logoDataUri && (
          <img src={brand.logoDataUri} alt="" className="max-h-11 max-w-[150px] object-contain" />
        )}
        <div>
          <p className="text-base font-semibold text-ink">{brand.displayName}</p>
          {brand.tagline && <p className="text-xs text-muted">{brand.tagline}</p>}
        </div>
      </div>
      <div className="text-right">
        <p
          className="text-lg font-bold tracking-wide"
          style={{ color: brand.accentColor ?? 'var(--primary)' }}
        >
          {title}
        </p>
        <p className="font-mono text-sm font-medium text-ink">{reference}</p>
      </div>
    </header>
  );
}

/** Who is billing, and who is being billed. */
export function Parties({
  brand,
  toTitle,
  toName,
  toSub,
}: {
  brand: DocumentBrand;
  toTitle: string;
  toName: string;
  toSub?: string | null;
}) {
  return (
    <div className="doc-keep grid gap-6 border-b border-line px-5 py-4 sm:grid-cols-2">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-faint">From</p>
        <p className="mt-1.5 text-sm font-medium text-ink">{brand.displayName}</p>
        {brand.supportEmail && <p className="text-sm text-muted">{brand.supportEmail}</p>}
        {brand.supportUrl && <p className="text-sm text-muted">{brand.supportUrl}</p>}
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-faint">{toTitle}</p>
        <p className="mt-1.5 text-sm font-medium text-ink">{toName}</p>
        {toSub && <p className="font-mono text-xs text-muted">{toSub}</p>}
      </div>
    </div>
  );
}

/** The facts a reader checks first, as label-over-value pairs. */
export function MetaGrid({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="doc-keep grid gap-x-6 gap-y-4 border-b border-line px-5 py-4 sm:grid-cols-3">
      {items.map((entry) => (
        <div key={entry.label}>
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-faint">
            {entry.label}
          </dt>
          <dd className="mt-1 text-sm text-ink">{entry.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The months this document was priced from.
 *
 * Nothing stores an OLT count — the API recomputes it from the organization's
 * chassis and the period, exactly as the amount was priced. When the total is
 * not a clean multiple of those OLT-months the per-OLT column reads "—" rather
 * than a figure that would not multiply back: see `documentLines` on the API.
 */
export function LineTable({
  lines,
  currency,
  totalMinor,
  fallbackLabel,
  accentColor,
}: {
  lines: DocumentLine[];
  currency: string;
  totalMinor: number;
  fallbackLabel: string;
  accentColor: string | null;
}) {
  return (
    <div>
      <Table>
        <THead>
          <th className="th">Description</th>
          <th className="th">OLTs</th>
          <th className="th">Per OLT</th>
          <th className="th text-right">Amount</th>
        </THead>
        <TBody>
          {lines.length === 0 ? (
            <TRow>
              <td className="td">{fallbackLabel}</td>
              <td className="td text-muted">—</td>
              <td className="td text-muted">—</td>
              <td className="td text-right font-medium text-ink">
                {formatMoney(totalMinor, currency)}
              </td>
            </TRow>
          ) : (
            lines.map((line) => (
              <TRow key={line.monthStart}>
                <td className="td">Platform service — {monthLabel(line.monthStart)}</td>
                <td className="td text-muted">{line.oltCount}</td>
                <td className="td text-muted">
                  {line.unitMinor === null ? '—' : formatMoney(line.unitMinor, currency)}
                </td>
                <td className="td text-right font-medium text-ink">
                  {line.amountMinor === null ? '—' : formatMoney(line.amountMinor, currency)}
                </td>
              </TRow>
            ))
          )}
        </TBody>
      </Table>
      <div className="doc-keep flex items-baseline justify-end gap-4 border-t border-line px-5 py-3.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-faint">Total</span>
        <span
          className="text-lg font-bold"
          style={{ color: accentColor ?? 'var(--primary)' }}
        >
          {formatMoney(totalMinor, currency, { symbol: true })}
        </span>
        {/* The code beside the symbol: this platform prices in two currencies,
            and "KSh 9,000" and "$9,000" must not be told apart by a glyph. */}
        <span className="text-xs text-faint">{currency.toUpperCase()}</span>
      </div>
    </div>
  );
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function monthLabel(ts: number): string {
  const d = new Date(ts);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * What has happened to this document, oldest first.
 *
 * Assembled by the API from the audit trail and the row's own timestamps —
 * neither alone is complete, because the monthly run raises invoices and
 * settlement pays them without a person doing either. The actor is only
 * present for a superadmin; a tenant sees what happened and when.
 */
export function HistoryList({ history }: { history: DocumentEvent[] }) {
  if (history.length === 0) {
    return <p className="px-5 py-4 text-sm text-muted">Nothing recorded yet.</p>;
  }
  return (
    <ol className="divide-y divide-line">
      {history.map((event, i) => (
        <li key={`${event.at}-${i}`} className="doc-keep flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-2.5">
          <Pill tone="neutral">{event.label}</Pill>
          <span className="text-sm text-muted">{formatTimestamp(event.at)}</span>
          {event.actor && <span className="text-xs text-faint">{event.actor}</span>}
          {event.detail && <span className="text-xs text-faint">{event.detail}</span>}
        </li>
      ))}
    </ol>
  );
}
